import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  Button,
  TextField,
  Chip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import ImageIcon from '@mui/icons-material/Image';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DiamondIcon from '@mui/icons-material/Diamond';
import { useLocation, useNavigate } from 'react-router';
import AiSidebar from './AiSidebar.tsx';
import BrandPreferencesContent from './BrandPreferencesContent.tsx';
import ModalForms from '../Shared/Modals/index.js';
import { AiActions, ConstantActions } from '../../Services/_actions/index.js';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import CloseIcon from '@mui/icons-material/Close';
import BeautifyText from '../AiChatbot/BeautifyText.tsx';
import { Dashboard } from '@mui/icons-material';
import { useWebSocketAiContext } from '../../utils/WebSocketContext.tsx';
import PresentationViewer from './PresentationViewer.tsx';
import CommonChatBubble from '../FlowBuilder/Common/ChatMessage/Commonchatbubble.tsx';
import { globalpermissions } from '../../Services/Permission.js';
import AiChipSelector from './AiChipSelector.tsx';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';

const starters = [
  'Summarise our value proposition in 3 lines',
  'Draft an investor email',
  'Compare us to top 3 competitors',
];

const modeTabs = [
  { label: 'Query', icon: <ChatIcon sx={{ fontSize: 14 }} /> },
  { label: 'Image', icon: <ImageIcon sx={{ fontSize: 14 }} /> },
  { label: 'Presentation', icon: <SlideshowIcon sx={{ fontSize: 14 }} /> },
  { label: 'Research', icon: <SearchIcon sx={{ fontSize: 14 }} /> },
  { label: 'Video Script', icon: <SmartToyIcon sx={{ fontSize: 14 }} /> },
];

// ── Types ────────────────────────────────────────────────────────────────────
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  imageUrl?: string;
  presentation?: {
    title: string;
    subtitle: string;
    slides: Slide[];
  } | null;
  pptUrl?: string;
  pptFilename?: string;
}

interface Slide {
  title: string;
  layout: 'title' | 'content' | 'two_column' | 'closing';
  bullets: string[];
  left_title: string;
  left_bullets: string[];
  right_title: string;
  right_bullets: string[];
  speaker_notes: string;
}

// ── Typing indicator ─────────────────────────────────────────────────────────
const TypingIndicator = () => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 2 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '10px',
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 16 }} />
    </Box>
    <Box
      sx={{
        px: 2,
        py: 1.4,
        bgcolor: '#fff',
        border: '1px solid #ebebf0',
        borderRadius: '4px 16px 16px 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            opacity: 0.5,
            animation: 'bounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes bounce': {
              '0%, 80%, 100%': { transform: 'scale(1)', opacity: 0.5 },
              '40%': { transform: 'scale(1.4)', opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  </Box>
);

const FileChip: React.FC<{ file: File; onRemove: () => void }> = ({ file, onRemove }) => {
  const [preview, setPreview] = useState<string | null>(null);
  const isImage = file.type.startsWith('image/');
  const ext = file.name.split('.').pop()?.toUpperCase().slice(0, 4) || 'FILE';

  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.8,
        bgcolor: '#f5f5f9',
        border: '1px solid #e8e8f0',
        borderRadius: '8px',
        px: 1,
        py: 0.5,
      }}
    >
      {isImage && preview ? (
        <Box
          component="img"
          src={preview}
          sx={{ width: 28, height: 28, borderRadius: '5px', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <Box
          sx={{
            width: 28,
            height: 28,
            bgcolor: '#e8eaff',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          {ext}
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 12,
          maxWidth: 100,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {file.name}
      </Typography>
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{ p: 0.2, color: '#bbb', '&:hover': { color: '#555' } }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const Ai = () => {
  const theme = useTheme();
  const action = AiActions();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackBar } = useSnackBar();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [message, setMessage] = useState('');
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const [conversationKey, setConversationKey] = useState(0);
  const [conversationList, setConversationList] = useState<any[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const userInfo = localStorage?.getItem('userInfo');
  const firstNAme = userInfo ? JSON.parse(userInfo)?.first_name : '';
  const CampanyID = userInfo ? JSON.parse(userInfo)?.profile?.organisation?.organisation_id : null;
  const ORGANIZATION_ID = CampanyID;
  const [instructionSet, setInstructionSet] = useState<any>({});
  const constActions = ConstantActions();
  const companyName = firstNAme || location.state?.companyName || 'Your Company';
  const [selectedCampaign, setSelectedCampaign] = useState<any>({});
  const [leadFormId, setLeadFormId] = useState<number | null>(null);
  const [lastChipSelection, setLastChipSelection] = useState<Selection | null>(null);
  // const [showChipSelector, setShowChipSelector] = useState(true);


  const breadcrumbItems = useMemo(() => {
    const base = [{ label: 'Machadalo Studio' }];
    if (!lastChipSelection) return base;

    const items = [...base];

    if (lastChipSelection.type) items.push({ label: lastChipSelection.type });
    if (lastChipSelection.category) items.push({ label: lastChipSelection.category });
    if (lastChipSelection.sub) items.push({ label: lastChipSelection.sub });
    if (lastChipSelection.focus) items.push({ label: lastChipSelection.focus });
    if (lastChipSelection.format) items.push({ label: lastChipSelection.format });

    return items;
  }, [lastChipSelection]);


  const handleCreateProject = async (name: string) => {
    try {
      const res = await action.createProject({
        project_name: name,
        organisation_id: ORGANIZATION_ID,
        campaign_id: 'MACTES8BF9',
      });

      if (res?.status === 200 || res?.status === 201) {
        const newProject: Project = {
          id: res?.data?.project_id || res?.data?.id || res?.data?._id || Date.now().toString(),
          name: res?.data?.project_name || res?.data?.name || name,
          emoji: '📁',
          chatCount: 0,
        };
        await fetchProjects();
        // setProjectList((prev) => [...prev, newProject]);
        setSelectedProjectId(newProject.id);
        showSnackBar('Project created successfully!', 'success');
      }
    } catch (err) {
      console.error('Failed to create project:', err);
      showSnackBar('Failed to create project. Please try again.', 'error');
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await action.getProjectList({
        organisation_id: 'MAC1421',
        campaign_id: 'MACTES8BF9',
      });

      const list = res?.data?.results || res?.data || [];

      const mapped: Project[] = list.map((p: any) => ({
        id: p.project_id || p._id,
        name: p.project_name || p.name,
        emoji: p.emoji || '📁',
        chatCount: p.chat_count || p.chatCount || 0,
      }));

      setProjectList(mapped);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const handleNewConversation = () => {
    setConversationKey((prev) => prev + 1);
    setCurrentConversationId(null);
    setActiveTab(0);
    setMessages([]);
    setAttachedFiles([]);
    setMessage('');
  };

  useEffect(() => {
    if (!selectedConversationId) return;

    (async () => {
      setIsLoading(true);
      setMessages([]);
      try {
        const history = await action.getChatHistory({
          parameters: { Dashboard: 'machadalo_studio' },
          ai_model: 'mdai',
          conversation_id: selectedConversationId,
        });

        if (history?.status === 200 && Array.isArray(history?.data)) {
          const loaded: ChatMessage[] = [];

          history.data.forEach((item: any, i: number) => {
            // User message
            if (item.query) {
              loaded.push({
                id: `${item._id}-user`,
                role: 'user',
                text: item.query,
                timestamp: new Date(item.created_at || Date.now()),
              });
            }
            // Assistant message
            if (item.response) {
              loaded.push({
                id: `${item._id}-ai`,
                role: 'assistant',
                text: item.response,
                timestamp: new Date(item.updated_at || item.created_at || Date.now()),
              });
            }
          });

          setMessages(loaded);
        }
      } catch (err) {
        console.error('Failed to load history:', err);
        showSnackBar('Could not load conversation history.', 'error');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [selectedConversationId]);

  const handleSettingsClick = () => {
    navigate('/r/machadalostudio/instructionsetform');
  };

  const fetchConversations = async () => {
    try {
      const res = await action.getConversationList({
        parameters: { Dashboard: 'machadalo_studio' },
      });
      setConversationList(res?.data?.results || res?.data || []);
    } catch (err) {
      console.error('Failed to fetch conversations', err);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchProjects();
  }, []);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const sidebarProps = useMemo(
    () => ({
      isMobile,
      navigate,
      onClose: () => setSidebarOpen(false),
      activeTab,
      onTabChange: setActiveTab,
    }),
    [isMobile, activeTab]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitQuery();
    }
  };


  useEffect(() => {
    const fetchInstructions = async () => {
      try {
        const res = await action.getOrganizationInstruction(ORGANIZATION_ID);

        const record = Array.isArray(res?.data)
          ? res.data[0]
          : res?.data?.[0] || res?.[0] || res;

        if (record?.instruction_set) {
          setInstructionSet(record.instruction_set);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      }
    };

    if (ORGANIZATION_ID) {
      fetchInstructions();
    }
  }, [ORGANIZATION_ID]);

  const getCurrentInstruction = () => {
    switch (activeTab) {
      case 0:
        return instructionSet?.chat_instruction || '';

      case 1:
        return instructionSet?.image_instruction || '';

      case 2:
        return instructionSet?.presentation_instruction || '';

      case 3:
        return instructionSet?.research_instruction || '';

      case 4:
        return instructionSet?.video_instruction || '';

      default:
        return '';
    }
  };

  const getCurrentType = () => {
    switch (activeTab) {
      case 0:
        return 'chat';

      case 1:
        return 'image';

      case 2:
        return 'presentation';

      case 3:
        return 'research';

      case 4:
        return 'video';

      default:
        return 'chat';
    }
  };

  const submitQuery = async () => {
    const currentQueryText = message.trim();

    if (
      (!currentQueryText && attachedFiles.length === 0) ||
      isLoading
    ) {
      return;
    }

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: currentQueryText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);

    setMessage('');
    setIsLoading(true);

    try {
      const currentInstruction = getCurrentInstruction();
      const currentType = getCurrentType();

      const formData = new FormData();

      // ── Core fields ──
      formData.append('organization_id', ORGANIZATION_ID);
      formData.append('project_id', selectedProjectId || '');
      formData.append('user_id', JSON.parse(userInfo || '{}')?.id || '');
      formData.append('query', currentQueryText);
      formData.append('action', currentType);           // presentation, chat, image etc
      formData.append('instruction', currentInstruction);

      // ── Lead form ──
      if (leadFormId) {
        formData.append('lead_form_id', String(leadFormId));
      }

      // ── Campaign ──
      if (selectedCampaign?.proposal_id) {
        formData.append('campaign_id', selectedCampaign.proposal_id);
      }

      // ── AiChipSelector selections ──
      if (lastChipSelection) {
        formData.append('type', lastChipSelection.type || '');
        formData.append('category', lastChipSelection.category || '');
        formData.append('sub_category', lastChipSelection.sub || '');
        formData.append('topic', lastChipSelection.focus || '');
        formData.append('format', lastChipSelection.format || '');
      }

      // ── URL (if user typed a URL in message) ──
      const urlMatch = currentQueryText.match(/https?:\/\/[^\s]+/);
      if (urlMatch) {
        formData.append('url', urlMatch[0]);
      }

      // ── Conversation ──
      if (currentConversationId) {
        formData.append('conversation_id', currentConversationId);
      }

      // ── Files ──
      attachedFiles.forEach((file) => {
        formData.append('files', file);
      });

      const res = await action.organizationChat(formData);

      const conversationId =
        res?.conversation_id || res?.data?.conversation_id;

      if (conversationId && !currentConversationId) {
        setCurrentConversationId(conversationId);
        fetchConversations();
      }

      const BASE_URL = 'https://devapi.machadalo.com';

      // ── Presentation Response ─────────────────────
      if (currentType === 'presentation') {
        const presentation = res?.data?.presentation;

        const pptUrl = res?.data?.file_url
          ? `${BASE_URL}${res.data.file_url}`
          : undefined;

        const assistantMsg: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          text: presentation
            ? '__PRESENTATION__'
            : 'Sorry, could not generate a presentation.',
          timestamp: new Date(),

          presentation: presentation
            ? {
              title: presentation.title,
              subtitle: presentation.subtitle,
              pptUrl,
              filename: res?.data?.filename,
            }
            : null,
        };

        setMessages((prev) => [...prev, assistantMsg]);
      }

      // ── Image Response ─────────────────────
      else if (currentType === 'image') {
        const imageUrl = res?.data?.image_url;

        const assistantMsg: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          text: imageUrl ? '' : 'Sorry, could not generate image.',
          timestamp: new Date(),
          imageUrl,
        };

        setMessages((prev) => [...prev, assistantMsg]);
      }

      // ── Chat / Research / Video Response ─────────────────────
      else {
        const aiText =
          res?.data?.response ||
          res?.response?.response;

        const assistantMsg: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          text: String(aiText || ''),
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, assistantMsg]);
      }

      setAttachedFiles([]);
    } catch (error) {
      console.error(error);

      showSnackBar(
        'Failed to send message. Please try again.',
        'error'
      );

      setMessages((prev) =>
        prev.filter((m) => m.id !== userMsg.id)
      );

      setMessage(currentQueryText);
    } finally {
      setIsLoading(false);
    }
  };

  const hasChatStarted = messages.length > 0;

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const toCommonMsg = (msg: ChatMessage) => ({
    id: msg.id,
    type: (msg.role === 'user' ? 'user' : 'ai') as 'user' | 'ai',
    content: msg.text,
    created_at: msg.timestamp?.toISOString(),
    presentation: msg.presentation,
  });

  // useEffect(() => {
  //   setShowChipSelector(false);
  // }, [activeTab]);


  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 0.5,
        height: { xs: 'calc(100vh - 30px)', sm: 'calc(100vh - 30px)' },
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 1, md: 'auto' },
        maxWidth: '100% !important',
      }}
    >
      <Grid container spacing={0.25} sx={{ flex: 1, overflow: 'hidden' }}>
        {isMobile && (
          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: '85%', maxWidth: 360 } }}
          >
            <AiSidebar
              {...sidebarProps}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              companyName={companyName}
              onNewConversation={handleNewConversation}
              conversationList={conversationList}
              onSelectConversation={(id) => {
                setSelectedConversationId(id);
                setCurrentConversationId(id);
                setMessage('');
                setAttachedFiles([]);
              }}
              selectedConversationId={selectedConversationId}
              projectList={projectList}
              selectedProjectId={selectedProjectId}
              onSelectProject={setSelectedProjectId}
              onCreateProject={handleCreateProject}
              onCampaignSelected={(val) => setSelectedCampaign(val)}
              onLeadFormFetched={(id) => setLeadFormId(id)}
            />
          </Drawer>
        )}

        {!isMobile && (
          <Grid item md={3} sx={{ height: '100%' }}>
            <Paper
              elevation={2}
              sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <AiSidebar
                {...sidebarProps}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                companyName={companyName}
                onNewConversation={handleNewConversation}
                conversationList={conversationList}
                onSelectConversation={(id) => {
                  setSelectedConversationId(id);
                  setCurrentConversationId(id);
                  setMessage('');
                  setAttachedFiles([]);
                }}
                selectedConversationId={selectedConversationId}
                projectList={projectList}
                selectedProjectId={selectedProjectId}
                onSelectProject={setSelectedProjectId}
                onCreateProject={handleCreateProject}
                onCampaignSelected={(val) => setSelectedCampaign(val)}
                onLeadFormFetched={(id) => setLeadFormId(id)}
              />
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={isMobile ? 12 : 9} sx={{ height: '100%', display: 'flex' }}>
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              mb: '5px',
              position: 'relative',
              bgcolor: '#f6f6f9',
            }}
          >
            {/* ── Top Bar ────────────────────────────────────────────────── */}
            <Box
              sx={{
                px: 2.5,
                py: 1.2,
                bgcolor: '#fff',
                borderBottom: '1px solid #ebebf0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(true)} sx={{ color: '#555', mr: 0.5 }}>
                    <MenuIcon />
                  </IconButton>
                )}
                {/* <Box>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e', lineHeight: 1.2 }}
                  >
                    Chat Studio
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>
                    {companyName} · Ask anything
                  </Typography>
                </Box> */}
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e', lineHeight: 1.2 }}>
                    Chat Studio
                  </Typography>
                  {/* ── Breadcrumbs ── */}
                  {breadcrumbItems.length > 1 && (
                    <DynamicBreadcrumbs items={breadcrumbItems} />
                  )}
                  {breadcrumbItems.length === 1 && (
                    <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>
                      {companyName} · Ask anything
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {constActions.userHasPermission([
                  globalpermissions?.machadaloStudioDashboard?.presentationBrand,
                ]) && (
                    <Button
                      startIcon={<DiamondIcon sx={{ fontSize: 15 }} />}
                      size="small"
                      onClick={() => setBrandModalOpen(true)}
                      sx={{
                        textTransform: 'none',
                        border: '1px solid #e0e0ee',
                        borderRadius: '8px',
                        color: '#444',
                        fontWeight: 500,
                        fontSize: 13,
                        px: 1.5,
                        bgcolor: '#fff',
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      Brand
                    </Button>
                  )}
                {constActions.userHasPermission([
                  globalpermissions?.machadaloStudioDashboard?.instruction_set,
                ]) && (
                    <IconButton
                      size="small"
                      sx={{
                        border: '1px solid #e0e0ee',
                        borderRadius: '8px',
                        bgcolor: '#fff',
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      <SettingsIcon
                        fontSize="small"
                        sx={{ color: '#666' }}
                        onClick={handleSettingsClick}
                      />
                    </IconButton>
                  )}
              </Box>
            </Box>

            {/* ── Chat / Welcome Area ────────────────────────────────────── */}
            <Box
              ref={chatBoxRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: { xs: 2, md: 4 },
                py: 3,
                bgcolor: '#f6f6f9',
                display: 'flex',
                flexDirection: 'column',
                // Smooth scrollbar
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#ddd', borderRadius: 3 },
              }}
            >
              {/* Welcome screen — shown only when no messages */}
              {/* {!hasChatStarted && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60%',
                  }}
                >
                  <Box
                    sx={{
                      width: 68,
                      height: 68,
                      borderRadius: '20px',
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      boxShadow: '0 8px 24px rgba(124,92,252,0.35)',
                    }}
                  >
                    <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 32 }} />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: 20, md: 26 },
                      fontWeight: 700,
                      color: '#1a1a2e',
                      mb: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    How can I help{' '}
                    <Box component="span" sx={{ color: 'primary.main' }}>
                      {companyName}
                    </Box>{' '}
                    today?
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: '#9e9e9e', mb: 3.5 }}>
                    Pick a starter or describe what you need.
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      maxWidth: 680,
                    }}
                  >
                    {starters.map((s) => (
                      <Box
                        key={s}
                        onClick={() => setMessage(s)}
                        sx={{
                          px: 2.5,
                          py: 2,
                          bgcolor: '#fff',
                          border: '1px solid #e8e8f0',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          maxWidth: 210,
                          fontSize: 13.5,
                          color: '#2a2a3e',
                          fontWeight: 500,
                          lineHeight: 1.45,
                          transition: 'all 0.15s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: '0 0 0 3px rgba(73,98,229,0.1)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {s}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )} */}
              {!hasChatStarted && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60%',
                  }}
                >
                  <Box
                    sx={{
                      width: 68,
                      height: 68,
                      borderRadius: '20px',
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      boxShadow: '0 8px 24px rgba(124,92,252,0.35)',
                    }}
                  >
                    <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 32 }} />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: 20, md: 26 },
                      fontWeight: 700,
                      color: '#1a1a2e',
                      mb: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    How can I help{' '}
                    <Box component="span" sx={{ color: 'primary.main' }}>
                      {companyName}
                    </Box>{' '}
                    today?
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: '#9e9e9e', mb: 3.5 }}>
                    Pick a starter, browse by category, or describe what you need.
                  </Typography>
                  <Box sx={{ width: '100%', maxWidth: 680, mt: 2 }}>
                    <AiChipSelector
                      mode={getCurrentType()}
                      onSelect={(prompt, meta) => {
                        setMessage(prompt);
                        setLastChipSelection(meta);
                      }}
                      onChange={(meta) => setLastChipSelection(meta)}
                    />
                  </Box>
                  {/* ── Quick starter chips (existing) ── */}
                  {/* {!showChipSelector && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        maxWidth: 680,
                        mb: 2,
                      }}
                    >
                      {starters.map((s) => (
                        <Box
                          key={s}
                          onClick={() => setMessage(s)}
                          sx={{
                            px: 2.5,
                            py: 2,
                            bgcolor: '#fff',
                            border: '1px solid #e8e8f0',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            maxWidth: 210,
                            fontSize: 13.5,
                            color: '#2a2a3e',
                            fontWeight: 500,
                            lineHeight: 1.45,
                            transition: 'all 0.15s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: '0 0 0 3px rgba(73,98,229,0.1)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          {s}
                        </Box>
                      ))}
                    </Box>
                  )} */}
                </Box>
              )}

              {/* Chat messages */}
              {hasChatStarted && (
                <Box sx={{ maxWidth: '100%', width: '100%', mx: 'auto' }}>
                  {/* <ChatBubble key={msg.id} msg={msg} companyName={companyName} /> */}

                  {messages.map((msg) => (
                    <CommonChatBubble
                      key={msg.id}
                      message={toCommonMsg(msg)}
                      aiAvatar={
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '10px',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 16 }} />
                        </Box>
                      }
                      userLabel={companyName?.[0]?.toUpperCase() || 'U'}
                      renderAiContent={(content) => (
                        <Box
                          sx={{
                            '& .MuiTypography-body1': {
                              fontSize: '13.5px !important',
                              lineHeight: '1.7 !important',
                              color: '#1a1a2e',
                            },
                            '& p': { margin: 0 },
                          }}
                        >
                          <BeautifyText text={content} />
                        </Box>
                      )}
                      imageSlot={
                        msg.imageUrl ? (
                          <Box
                            sx={{
                              mt: 1,
                              borderRadius: '14px',
                              overflow: 'hidden',
                              border: '1px solid #e8e8f0',
                              maxWidth: 420,
                            }}
                          >
                            <Box
                              component="img"
                              src={msg.imageUrl}
                              alt="Generated"
                              sx={{
                                width: '100%',
                                display: 'block',
                                objectFit: 'cover',
                              }}
                            />
                          </Box>
                        ) : undefined
                      }
                      presentationSlot={
                        msg.presentation ? (
                          <PresentationViewer
                            title={msg.presentation.title}
                            subtitle={msg.presentation.subtitle}
                            pptUrl={msg.presentation.pptUrl}
                            pptFilename={msg.presentation.filename}
                          />
                        ) : undefined
                      }
                      formatTime={(d) =>
                        new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      }
                      variant="studio"
                    />
                  ))}
                  {isLoading && <TypingIndicator />}
                  <div ref={messagesEndRef} />
                </Box>
              )}
            </Box>

            {/* ── Bottom Input Area ──────────────────────────────────────── */}
            <Box sx={{ px: 2.5, pb: 1.5, pt: 1, bgcolor: '#f6f6f9', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                {modeTabs.map((t, i) => (
                  <Chip
                    key={t.label}
                    label={t.label}
                    icon={React.cloneElement(t.icon, {
                      sx: {
                        fontSize: 14,
                        color: i === activeTab ? '#fff !important' : '#888 !important',
                      },
                    })}
                    onClick={() => {
                      setActiveTab(i);
                      setLastChipSelection(null);
                    }}
                    size="small"
                    sx={{
                      borderRadius: '999px',
                      bgcolor: i === activeTab ? 'primary.main' : '#fff',
                      color: i === activeTab ? '#fff' : '#444',
                      border: `1px solid ${i === activeTab ? 'transparent' : '#e0e0ee'}`,
                      fontWeight: i === activeTab ? 700 : 500,
                      fontSize: 12,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #e0e0ee',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}
              >
                {/* ── File Preview Row ── */}
                {attachedFiles.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      px: 2,
                      pt: 1.2,
                      pb: 0.5,
                      borderBottom: '1px solid #f0f0f5',
                    }}
                  >
                    {attachedFiles.map((file, idx) => (
                      <FileChip
                        key={idx}
                        file={file}
                        onRemove={() =>
                          setAttachedFiles((prev) => prev.filter((_, i) => i !== idx))
                        }
                      />
                    ))}
                  </Box>
                )}

                {/* ── Text Input Row ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Message Chat…  (Shift+Enter for newline)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: 13.5,
                        color: '#2a2a3e',
                        '&::placeholder': { color: '#bbb', opacity: 1 },
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleFileClick}
                    sx={{ color: '#bbb', '&:hover': { color: '#666' } }}
                  >
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={(!message.trim() && attachedFiles.length === 0) || isLoading}
                    onClick={submitQuery}
                    sx={{
                      bgcolor:
                        (message.trim() || attachedFiles.length > 0) && !isLoading
                          ? 'primary.main'
                          : '#e8e8f0',
                      color:
                        (message.trim() || attachedFiles.length > 0) && !isLoading
                          ? '#fff'
                          : '#bbb',
                      borderRadius: '10px',
                      width: 34,
                      height: 34,
                      '&.Mui-disabled': { bgcolor: '#e8e8f0', color: '#bbb' },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={16} sx={{ color: '#bbb' }} />
                    ) : (
                      <SendIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Brand Preferences Modal ───────────────────────────────────── */}
      <ModalForms
        show={brandModalOpen}
        handleClose={() => setBrandModalOpen(false)}
        modalHeader=""
        size="md"
        modalBody={
          <BrandPreferencesContent
            onCancel={() => setBrandModalOpen(false)}
            onSave={() => setBrandModalOpen(false)}
            onReset={() => { }}
            companyName={companyName}
          />
        }
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const newFiles = Array.from(e.target.files || []);
          setAttachedFiles((prev) => {
            const existing = new Set(prev.map((f) => f.name + f.size));
            return [...prev, ...newFiles.filter((f) => !existing.has(f.name + f.size))];
          });
          e.target.value = '';
        }}
      />
    </Container>
  );
};

export default Ai;
